import { useEffect, useState } from "react";
import type { ChangeBrief } from "../../shared/schema";
import { CheckIcon, CloseIcon, PhoneIcon, ShieldIcon } from "../icons";

interface PreviewDialogProps {
  change: ChangeBrief;
  isOpen: boolean;
  onClose: () => void;
  onRunDryInterview: () => void;
}

export function PreviewDialog({ change, isOpen, onClose, onRunDryInterview }: PreviewDialogProps) {
  const [checked, setChecked] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setChecked(false);
      setRunning(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const run = () => {
    setRunning(true);
    window.setTimeout(onRunDryInterview, 850);
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <section className="modal preview-modal" role="dialog" aria-modal="true" aria-labelledby="preview-title">
        <header>
          <div><h2 id="preview-title">Preview owner interview</h2><p>Review exactly what the owner will hear. This preview cannot place a call.</p></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close preview"><CloseIcon /></button>
        </header>
        <div className="preview-layout">
          <div className="script-panel">
            <span className="modal-label">CALL TASK</span>
            <p><b>Identity.</b> Ask for {change.owner.name}. Disclose that this is an AI call from CALL-E on behalf of the data platform team.</p>
            <p><b>Consent.</b> Ask whether the owner agrees to continue. End politely if they decline.</p>
            <p><b>Context.</b> Explain that <code>{change.sourceField}</code> would become <code>{change.targetField}</code>.</p>
            <ol>{change.questions.map((question) => <li key={question.id}>{question.text}</li>)}</ol>
            <p><b>Boundary.</b> Read back conditions. Do not promise or execute a migration; the result is evidence for later human review.</p>
          </div>
          <div className="preview-controls">
            <div className="preview-recipient"><PhoneIcon /><div><span>Authorized recipient</span><strong>{change.owner.name}</strong><small>+1 ••• ••• 0148</small></div></div>
            <div className="preview-safety"><ShieldIcon /><div><strong>Dry-run mode</strong><p>The demo creates local evidence only. No API key, credits, or phone network are used.</p></div></div>
            <label className="review-check"><input type="checkbox" checked={checked} onChange={(event) => setChecked(event.target.checked)} /><span><CheckIcon /></span>I reviewed the script and recipient.</label>
            <button className="run-demo" type="button" disabled={!checked || running} onClick={run}>{running ? <span className="spinner" /> : <PhoneIcon />}{running ? "Simulating interview…" : "Run dry interview"}</button>
            <p className="live-note">A real call requires server-side configuration plus the exact confirmation phrase <code>PLACE ONE AUTHORIZED CALL</code>.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
